import { cn } from '@cotera/client/v0/lib/utils';
import {
  CHART_ARTIFACT_TYPE,
  CHART_DASHBOARD_ARTIFACT_TYPE,
  isChartArtifactPayload,
  isChartDashboardPayload,
} from '@cotera/api';
import {
  ChartInlinePreview,
  ChartDashboardSnippet,
} from '@cotera/client/v0/components/charts';
import { useArtifactPanel } from './artifact-panel-context';
import { useArtifactTitle } from './use-artifact-title';
import { ArtifactIcon } from './artifacts/artifact-type-icon';

// Re-exported so existing consumers can keep importing it from `artifact-snippet`.
export { ArtifactIcon };

type ArtifactSnippetProps = {
  artifactId: string;
  artifactType: string;
  payload?: unknown;
  className?: string;
};

const formatTypeLabel = (artifactType: string): string => {
  if (artifactType.length === 0) {
    return artifactType;
  }
  return artifactType.charAt(0).toUpperCase() + artifactType.slice(1);
};

/**
 * Inline card shown in chat messages for tool outputs that produce artifacts.
 * Clicking opens the artifact: side panel on desktop, full-screen sheet on mobile.
 */
export const ArtifactSnippet: React.FC<ArtifactSnippetProps> = ({
  artifactId,
  artifactType,
  payload,
  className,
}) => {
  const artifactPanel = useArtifactPanel();
  const title = useArtifactTitle({
    artifactId,
    type: artifactType,
    payload,
  });

  const isClickable = artifactPanel !== null;
  const handleOpen = () => {
    artifactPanel?.focusArtifact(artifactId);
  };

  if (artifactType === CHART_ARTIFACT_TYPE && isChartArtifactPayload(payload)) {
    return (
      <ChartInlinePreview
        payload={payload}
        className={className}
        onOpen={isClickable ? handleOpen : undefined}
      />
    );
  }

  if (
    artifactType === CHART_DASHBOARD_ARTIFACT_TYPE &&
    isChartDashboardPayload(payload)
  ) {
    return (
      <ChartDashboardSnippet
        payload={payload}
        className={className}
        onOpen={isClickable ? handleOpen : undefined}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={isClickable ? handleOpen : undefined}
      disabled={!isClickable}
      className={cn(
        'group inline-flex max-w-full items-center gap-3 rounded-lg border border-input bg-background px-3 py-2.5 text-left shadow-xs transition-colors',
        isClickable &&
          'cursor-pointer hover:bg-muted/60 hover:border-border focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        !isClickable && 'cursor-default',
        className
      )}
      aria-label={isClickable ? `Open ${title}` : title}
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-md border border-input bg-muted/50 text-muted-foreground">
        <ArtifactIcon artifactType={artifactType} className="size-4" />
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium text-foreground">
          {title}
        </span>
        <span className="truncate text-xs text-muted-foreground">
          {formatTypeLabel(artifactType)}
        </span>
      </span>
    </button>
  );
};
